import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { departmentsApi } from '@api/departments'
import { toast } from 'react-toastify'

/**
 * Departments Hook
 */
export const useDepartments = () => {
    const queryClient = useQueryClient()

    // Get all departments
    const {
        data: departmentsData,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ['departments'],
        queryFn: () => departmentsApi.getAll(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })

    // Create department mutation
    const createMutation = useMutation({
        mutationFn: (data) => departmentsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('Department created')
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to create department')
        },
    })

    // Update department mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => departmentsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('Department updated')
        },
        onError: () => {
            toast.error('Failed to update department')
        },
    })

    // Delete department mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => departmentsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('Department deleted')
        },
        onError: () => {
            toast.error('Failed to delete department')
        },
    })

    // Navigate submission to department mutation
    const navigateMutation = useMutation({
        mutationFn: ({ submissionId, departmentId, notes }) =>
            departmentsApi.navigate(submissionId, departmentId, notes),
        onSuccess: () => {
            queryClient.invalidateQueries(['submissions'])
            queryClient.invalidateQueries(['submission'])
            queryClient.invalidateQueries(['stats'])
            toast.success('Submission navigated to department')
        },
        onError: () => {
            toast.error('Failed to navigate submission')
        },
    })

    // Add comment mutation
    const commentMutation = useMutation({
        mutationFn: ({ submissionId, comment }) =>
            departmentsApi.addComment(submissionId, comment),
        onSuccess: () => {
            queryClient.invalidateQueries(['submission'])
            queryClient.invalidateQueries(['comments'])
            queryClient.invalidateQueries(['stats'])
            toast.success('Comment added')
        },
        onError: () => {
            toast.error('Failed to add comment')
        },
    })

    // Add user to department mutation
    const addUserMutation = useMutation({
        mutationFn: ({ departmentId, userData }) =>
            departmentsApi.addUser(departmentId, userData),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('User added to department')
        },
        onError: () => {
            toast.error('Failed to add user')
        },
    })

    // Update department user mutation
    const updateUserMutation = useMutation({
        mutationFn: ({ userId, userData }) =>
            departmentsApi.updateUser(userId, userData),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('User updated')
        },
        onError: () => {
            toast.error('Failed to update user')
        },
    })

    // Delete department user mutation
    const deleteUserMutation = useMutation({
        mutationFn: (userId) =>
            departmentsApi.deleteUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries(['departments'])
            toast.success('User deleted')
        },
        onError: () => {
            toast.error('Failed to delete user')
        },
    })

    // Set department leader mutation
    const setLeaderMutation = useMutation({
        mutationFn: ({ userId, isLeader }) =>
            departmentsApi.setLeader(userId, isLeader),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['departments'])
            const msg = data?.message || 'Leader status updated'
            toast.success(msg)
        },
        onError: () => {
            toast.error('Failed to update leader status')
        },
    })

    return {
        departments: departmentsData?.data || [],
        isLoading,
        isError,
        error,
        refetch,
        createDepartment: createMutation.mutate,
        updateDepartment: updateMutation.mutate,
        deleteDepartment: deleteMutation.mutate,
        navigateSubmission: navigateMutation.mutate,
        addComment: commentMutation.mutate,
        addUser: addUserMutation.mutate,
        updateUser: updateUserMutation.mutate,
        deleteUser: deleteUserMutation.mutate,
        setLeader: setLeaderMutation.mutate,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isNavigating: navigateMutation.isPending,
        isCommenting: commentMutation.isPending,
        isSettingLeader: setLeaderMutation.isPending,
    }
}

/**
 * Department Comments Hook
 */
export const useDepartmentComments = (submissionId) => {
    const {
        data: commentsData,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['comments', submissionId],
        queryFn: () => departmentsApi.getComments(submissionId),
        enabled: !!submissionId,
        staleTime: 30 * 1000,
    })

    return {
        comments: commentsData?.data || [],
        isLoading,
        isError,
    }
}

export default useDepartments
